import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import DatePicker, {
  CalendarDay,
  useDefaultStyles,
} from 'react-native-ui-datepicker';
import { useSelector } from 'react-redux';
import { Divider, Spinner } from '@ui-kitten/components';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../Config/constants';
import { pluralize } from '../../../Helper';
import DateHelper from '../../../Helper/Date';
import StoreHelper from '../../../Helper/Store';
import useAlert from '../../../Hooks/useAlert';
import useReservationBooking from '../../../Hooks/useReservationBooking';
import { EventTimeSlot } from '../../../RTK/api/events/types';
import {
  useGetAvailableByRangeQuery,
  useGetTableAvailabilityQuery,
  useGetTableReservableDaysQuery,
} from '../../../RTK/api/reservations';
import { selectReservationStoreFilter } from '../../../RTK/filter/selectors';
import CheckAvailability from '../../../Screens/ReservationEventDetails/components/CheckAvailability';
import OptionBox from '../../../Screens/ReservationStoreDetails/components/OptionBox';
import themeColor from '../../../Theme/colors';
import themeStyle, {
  componentStyles,
  globalSpacing,
} from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import { DBDATE, DBTIME } from '../../../types/date';

import DropdownPopover from '../../DropdownPopover';
import { DropdownPopoverDataItem } from '../../DropdownPopover/types';
import ReadMore from '../../ReadMore';
import { textCategory } from '../../Reservation/constants';
import ReservationOverview from '../../Reservation/Overview';
import ResponsiveText from '../../ResponsiveText';
import { ModalManager } from '../../Web/Modal/hooks/useModalManager';

import modals from '../modals';
import RNUIDatePickerDay from '../RNUIDatePickerDay';

import TimeItem from './TimeItem';
import { RenderProps, ReservationType } from './types';

const DISPLAY_DATE_FORMAT = 'MMMM DD, YYYY';

dayjs.extend(customParseFormat);

const BookReservationRender = ({
  filter,
  itemInfo,
  storeInfo,
  scrollToSelectTime,
}: RenderProps) => {
  const alert = useAlert();
  const selectTimeRef = useRef<View>();
  const selectTimeYPos = useRef<number>(0);
  const defaultStyles = useDefaultStyles();
  const { availabilityRef, selectBookingSchedule } = useReservationBooking();
  const storeFilter = useSelector(selectReservationStoreFilter);

  const isEvent = itemInfo.type === ReservationType.EVENT;
  const isTable = itemInfo.type === ReservationType.DINING;
  const {
    availableDays,
    eventDescription,
    eventEndDate,
    eventStartDate,
    eventTickets,
    eventTimeSlots,
    itemId,
    sizeMax,
    sizeMin,
    tableBlockoutDates,
    tableDescription,
    tableExperience,
    tableTimeSlots,
  } = useMemo(
    () => ({
      availableDays: isEvent
        ? itemInfo.available_days
        : StoreHelper.getStoreAvailableDates(storeInfo.store_hours),
      eventDescription: isEvent ? itemInfo.description : '',
      eventEndDate: isEvent ? itemInfo.end_date : null,
      eventStartDate: isEvent ? itemInfo.start_date : null,
      eventTickets: isEvent ? itemInfo.tickets : [],
      eventTimeSlots: isEvent ? itemInfo.time_slots : [],
      itemId: isTable ? itemInfo.item.id : itemInfo.id,
      sizeMax: isTable ? itemInfo.maximum : undefined,
      sizeMin: isTable ? itemInfo.minimum : undefined,
      tableBlockoutDates: isTable ? itemInfo.blockout_dates : [],
      tableDescription: isTable ? itemInfo.item.description : '',
      tableExperience: isTable ? itemInfo.experiences : [],
      tableTimeSlots: isTable ? itemInfo.time_slots : [],
    }),
    [isEvent, isTable, itemInfo, storeInfo.store_hours],
  );
  const firstDate = useMemo(() => {
    if (isTable) return dayjs().add(1, 'day'); // should not be able to book same day
    const { start } = DateHelper.getEventScheduleInfo(
      eventStartDate,
      eventEndDate,
      eventTimeSlots,
      availableDays,
    );
    return start;
  }, [availableDays, eventEndDate, eventStartDate, eventTimeSlots, isTable]);

  const [showDate, setShowDate] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    filter.date ? dayjs(filter.date) : firstDate,
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    selectedDate.month(),
  );
  const [selectedYear, setSelectedYear] = useState<number>(selectedDate.year());
  const [selectedSize, setSelectedSize] = useState<DropdownPopoverDataItem>({
    label: `${filter.size} ${pluralize(filter.size, 'guest')}`,
    value: filter.size,
  });
  const [soldOutDates, setSoldOutDates] = useState<string[]>([]);

  const isFuture = selectedDate.isAfter(dayjs());

  const { data: tableReservableDates } = useGetTableReservableDaysQuery(
    { payload: storeInfo.id },
    { skip: !storeInfo.id },
  );

  const diningServiceGroup = useMemo(
    () =>
      lodashFind(tableReservableDates?.dining_service_group, {
        end_time: storeFilter.tableServingTime,
      }),
    [storeFilter.tableServingTime, tableReservableDates?.dining_service_group],
  );

  const timeEnd = useMemo(() => {
    if (!constants.USE_DINING_SERVICE_GROUP || !diningServiceGroup?.end_time) {
      return undefined;
    }
    return diningServiceGroup.end_time;
  }, [diningServiceGroup?.end_time]);

  const {
    currentData: data = {
      blockout_dates: tableBlockoutDates,
      experiences: tableExperience,
      time_slots: tableTimeSlots,
    },
    isFetching,
  } = useGetTableAvailabilityQuery(
    {
      payload: {
        id: itemId,
        date: selectedDate.format(constants.DBDATE_FORMAT) as DBDATE,
        time: filter.time as DBTIME,
        time_end: timeEnd,
        party_size: selectedSize.value,
      },
    },
    {
      // skip if any of the ff: is true
      skip:
        isEvent || // event
        !isFuture, // today and past date
    },
  );
  const selectedMonthAndYearDayjs = dayjs()
    .set('month', selectedMonth)
    .set('year', selectedYear);
  const isCurrentMonth = selectedMonth === dayjs().month();
  const rangeFrom = isCurrentMonth
    ? firstDate
    : selectedMonthAndYearDayjs.startOf('month');
  const { data: availableRange, isSuccess: isAvailableRangeSuccess } =
    useGetAvailableByRangeQuery(
      {
        payload: {
          id: itemId,
          from: rangeFrom.format(constants.DBDATE_FORMAT) as DBDATE,
          to: selectedMonthAndYearDayjs
            .endOf('month')
            .format(constants.DBDATE_FORMAT) as DBDATE,
        },
      },
      {
        skip: isEvent || rangeFrom.isBefore(dayjs(), 'month'), // range outdated
      },
    );

  // Effect for handling no available turning it to sold out dates
  useEffect(() => {
    if (isAvailableRangeSuccess) {
      const availableRangeArr = Object.values(availableRange);
      if (availableRangeArr.length !== 0) {
        const noAvailableDates = lodashFilter(availableRangeArr, {
          available: 0,
        }).map((d) => d.date);
        setSoldOutDates((prev) => [...new Set([...prev, ...noAvailableDates])]);
      }
    }
  }, [availableRange, firstDate, isAvailableRangeSuccess]);

  // to filter out outdated time slot if today
  const selectedHours = useMemo(() => {
    let hours = [];
    if (isFetching) return (hours = []);
    else if (isFuture) {
      hours = data?.time_slots;
    } else {
      // today and past date
      const todayNextTsDayjs = DateHelper.getToNextSlot(dayjs());
      hours = lodashFilter(data?.time_slots, (ts) => {
        const tsDayjs = dayjs(ts, constants.DBTIME_FORMAT);
        return todayNextTsDayjs.isSameOrBefore(tsDayjs);
      });
    }
    return hours;
  }, [isFetching, isFuture, data?.time_slots]);

  const partySizes = useMemo(() => {
    if (!isTable) return [];
    return Array.from({ length: sizeMax - sizeMin + 1 }, (_, i) => {
      const count = sizeMin + i;
      return {
        label: `${count} ${pluralize(count, 'guest')}`,
        value: count,
      };
    });
  }, [isTable, sizeMax, sizeMin]);

  const PartySizeTrigger = useMemo(
    () => (
      <OptionBox
        label="Party Size"
        placeholder="Party of"
        value={selectedSize}
        withBorder={false}
      />
    ),
    [selectedSize],
  );

  const _onProceed = useCallback(
    ({
      selectedExpTs,
      selectedEventTs,
    }: {
      selectedExpTs?: string;
      selectedEventTs?: EventTimeSlot;
    }) => {
      if (
        isTable &&
        (selectedSize.value < sizeMin || selectedSize.value > sizeMax)
      ) {
        // if table experience and party size is not within min and max pax
        return alert(
          'Invalid Party Size',
          'The selected experience has a minimum and maximum number of guests allowed. Please adjust your party size to proceed.',
          { web: { buttonStatus: 'info' } },
        );
      }
      const hide = constants.isWeb ? ModalManager.hide : modals.hide;
      const dateAndTime = {
        date: selectedDate.format(constants.DBDATE_FORMAT) as DBDATE,
        time: (isTable ? selectedExpTs : selectedEventTs.start_time) as DBTIME,
      };
      selectBookingSchedule({
        itemInfo: {
          ...(isTable
            ? {
                ...itemInfo,
                ...data, // update outdated value on itemInfo when date is changed
                experienceSelectedTs: selectedExpTs,
              }
            : { ...itemInfo, eventSelectedTs: selectedEventTs }),
        },
        dateAndTime,
        size: selectedSize.value,
        storeInfo: storeInfo,
        onError: () => {
          setSoldOutDates((prev) => [
            ...prev,
            `${dateAndTime.date} ${dateAndTime.time}`,
          ]);
        },
        onSuccess: (result) => {
          const response = { success: true, data: result };
          hide(
            modals.BOOK_RESERVATION,
            constants.isWeb ? { payload: response } : response,
          );
        },
      });
    },
    [
      data,
      isTable,
      itemInfo,
      selectedSize.value,
      selectedDate,
      sizeMax,
      sizeMin,
      storeInfo,
      alert,
      selectBookingSchedule,
    ],
  );

  const DatePickerDay = useCallback(
    (day: CalendarDay) => {
      const formattedDate = dayjs(day.date).format(constants.DBDATE_FORMAT);
      return (
        <RNUIDatePickerDay
          {...day}
          available={availableRange?.[formattedDate]?.available}
          soldOutDates={soldOutDates}
        />
      );
    },
    [availableRange, soldOutDates],
  );

  const _renderTimeSelection = useCallback(() => {
    const timeSlots = isTable ? selectedHours : eventTimeSlots;
    if (isFetching) {
      return (
        <View
          style={[
            themeStyle.pageVerticalSpacingMedium,
            themeStyle.alignItemsCenter,
          ]}
        >
          <Spinner size="medium" status="success" />
        </View>
      );
    } else if (lodashIsEmpty(timeSlots)) {
      return (
        <ResponsiveText
          category={textCategory.SMALL_BODY}
          style={[themeStyle.pageVerticalSpacingMedium, themeStyle.textCenter]}
        >
          No slots available
        </ResponsiveText>
      );
    }
    return timeSlots.map((ts) => {
      // disable if this loop time slot is on the array of sold out dates
      const disabled = soldOutDates.includes(
        `${selectedDate.format(constants.DBDATE_FORMAT)} ${
          isTable ? ts : ts.start_time
        }`,
      );
      // select experience/event base on selected time slot
      const expOrEvt = isTable
        ? lodashFilter(data.experiences, (e) => e.time_slots.includes(ts))
        : lodashFilter(eventTickets, (e) => e.time_slots.includes(ts.id));
      // map the data to return array of string event/experience name (expOrEvtItem return ['Ala Carte', "VIP", 'etc'])
      const expOrEvtItem = [
        ...new Set(
          expOrEvt
            .map((e) => e.item?.name || e.items?.map?.((e) => e.name))
            .flatMap((e) => e),
        ),
      ];
      const value = DateHelper.getDayjsAtTime(
        isTable ? ts : ts.start_time,
        selectedDate.toISOString(),
      );
      return (
        <View key={ts.id || ts} style={themeStyle.pageVerticalSpacingSmall}>
          <TimeItem
            description={
              expOrEvtItem.length === 1
                ? expOrEvtItem[0]
                : 'Multiple Experiences'
            }
            disabled={disabled}
            value={value}
            onProceed={() =>
              _onProceed(
                isTable ? { selectedExpTs: ts } : { selectedEventTs: ts },
              )
            }
          />
        </View>
      );
    });
  }, [
    data?.experiences,
    eventTickets,
    eventTimeSlots,
    isFetching,
    isTable,
    selectedDate,
    selectedHours,
    soldOutDates,
    _onProceed,
  ]);

  const _isDateEnabled = useCallback(
    (date: Dayjs) =>
      !data?.blockout_dates?.includes?.(
        date.format(constants.DBDATE_FORMAT) as DBDATE,
      ),
    [data?.blockout_dates],
  );
  const _onChange = useCallback(({ date }) => setSelectedDate(dayjs(date)), []);
  const _onMonthChange = useCallback(
    (month: number) => {
      const firstDayOfMonth = dayjs().set('month', month).startOf('month');
      setSelectedDate(
        firstDayOfMonth.isBefore(firstDate) ? firstDate : firstDayOfMonth,
      );
      setSelectedMonth(month);
    },
    [firstDate],
  );
  const _onYearChange = useCallback(
    (year: number) => setSelectedYear(year),
    [],
  );

  return (
    <View style={themeStyle.flex1}>
      {!constants.isWeb && (
        <Fragment>
          <ResponsiveText category={textCategory.HEADING}>
            {storeInfo.name}
          </ResponsiveText>
          <Divider
            style={[
              themeStyle.divider,
              themeStyle.spacingTopMedium,
              themeStyle.spacingBottomMedium,
            ]}
          />
        </Fragment>
      )}
      <ResponsiveText
        category={textCategory.HEADING}
        style={[themeStyle.bold, themeStyle.spacingBottomSmall]}
      >
        {itemInfo.name}
      </ResponsiveText>
      <ReadMore
        category={textCategory.SMALL_BODY}
        status="info"
        contentContainerStyle={themeStyle.spacingBottomSmall}
        maxChar={75}
        value={eventDescription || tableDescription}
      />
      <View style={themeStyle.spacingBottomSmall}>
        <ReservationOverview
          contentContainerStyle={themeStyle.spacingBottomXXSmall}
          textCategory={textCategory.SMALL_BODY}
          {...(isTable
            ? {
                tableData: {
                  is_communal: itemInfo.is_communal,
                  experiences: tableExperience,
                  maximum: itemInfo.maximum,
                  minimum: itemInfo.minimum,
                },
              }
            : {
                eventData: {
                  available_days: availableDays,
                  end_date: eventEndDate,
                  tickets: eventTickets,
                  time_slots: eventTimeSlots,
                  start_date: eventStartDate,
                },
              })}
        />
      </View>
      <View style={localStyles.dateAndSizeBoxContainer}>
        <View
          dataSet={{ media: localStyleIds.dateAndPartySizeContainer }}
          style={localStyles.dateAndPartySizeContainer}
        >
          <OptionBox
            iconSize={20}
            label="Date"
            value={{
              label: selectedDate.format(DISPLAY_DATE_FORMAT),
              value: selectedDate.format(constants.DBDATE_FORMAT),
            }}
            onPress={() => setShowDate((prev) => !prev)}
            withBorder={false}
          />
        </View>
        <View style={[themeStyle.divider, localStyles.divider]} />
        <View
          dataSet={{ media: localStyleIds.dateAndPartySizeContainer }}
          style={localStyles.dateAndPartySizeContainer}
        >
          <DropdownPopover
            trigger={PartySizeTrigger}
            value={selectedSize}
            data={partySizes}
            selectionType="color"
            onSelect={setSelectedSize}
          />
        </View>
      </View>
      {/* Date & Time Picker */}
      <View style={themeStyle.flex1}>
        {/* Date Picker */}
        {showDate && (
          <View style={themeStyle.flex1}>
            <ResponsiveText
              appearance="hint"
              category={textCategory.SMALL_BODY}
              style={themeStyle.spacingBottomXSmall}
            >
              Select Date:
            </ResponsiveText>
            <View
              dataSet={{
                media: `
                  ${localStyleIds.dateContainer}
                  ${localStyleIds.dateAndTimePickerContainer}
                `,
              }}
              style={[
                localStyles.dateContainer,
                localStyles.dateAndTimePickerContainer,
              ]}
            >
              <DatePicker
                mode="single"
                date={selectedDate}
                containerHeight={280}
                enabledDates={_isDateEnabled}
                minDate={firstDate}
                maxDate={
                  isEvent
                    ? dayjs(itemInfo.end_date)
                    : dayjs().add(
                        tableReservableDates?.max_reservable_days,
                        'days',
                      )
                }
                components={{ Day: DatePickerDay }}
                styles={{
                  ...defaultStyles,
                  ...componentStyles.eventDatepicker,
                }}
                onChange={_onChange}
                onMonthChange={_onMonthChange}
                onYearChange={_onYearChange}
                showOutsideDays
              />
            </View>
          </View>
        )}
        {/* Time Picker */}
        <View
          ref={selectTimeRef}
          style={themeStyle.flex1}
          onLayout={(event) => {
            const layout = event.nativeEvent.layout;
            selectTimeYPos.current = layout.y;
          }}
        >
          <ResponsiveText
            appearance="hint"
            category={textCategory.SMALL_BODY}
            style={themeStyle.spacingBottomXSmall}
          >
            Select Time:
          </ResponsiveText>
          {isFetching ? (
            <ActivityIndicator size="large" />
          ) : (
            <ScrollView
              contentContainerStyle={localStyles.timePickerScroll}
              onLayout={() => {
                scrollToSelectTime(
                  constants.isWeb
                    ? selectTimeRef.current
                    : selectTimeYPos.current,
                );
              }}
            >
              <View
                dataSet={{ media: localStyleIds.dateAndTimePickerContainer }}
                style={localStyles.dateAndTimePickerContainer}
              >
                {_renderTimeSelection()}
              </View>
            </ScrollView>
          )}
        </View>
      </View>
      <CheckAvailability ref={availabilityRef} />
    </View>
  );
};

const { ids: localStyleIds, styles: localStyles } = StyleSheet.create({
  dateAndPartySizeContainer: {
    ...themeStyle.flex1,
    ...themeStyle.pageHorizontalSpacing,
    [`@media ${breakpoints.xs}`]: {
      paddingHorizontal: 5,
    },
  },
  dateContainer: {
    [`@media ${breakpoints.sm}`]: {
      paddingHorizontal: globalSpacing * 2,
    },
  },
  dateAndSizeBoxContainer: {
    ...themeStyle.flexDirectionRowCenter,
    ...themeStyle.spacingBottomMedium,
    borderWidth: 1,
    borderColor: themeColor.gray,
  },
  divider: { ...themeStyle.divider, width: 1, height: 50 },
  dateAndTimePickerContainer: {
    [`@media ${breakpoints.md}`]: {
      width: '80%',
      alignSelf: 'center',
    },
  },
  timePickerScroll: {
    flexGrow: 1,
  },
});

export default BookReservationRender;
